/**
 * The prototype's fixture workspace.
 *
 * ── Every status literal here is REAL ──
 *
 * The types below mirror `packages/core/src/verification/product-model.ts` and the
 * sibling modules: `PASS | FAIL | UNKNOWN | NOT_RUN`, `deterministic | semantic |
 * human | imported`, `open | accepted_exception | remediated | dismissed`,
 * `accept_exception | request_evidence | approve_remediation | reject_remediation
 * | dismiss`, the eight review-case states, the six native connector providers,
 * the nine scorecard metric ids. That is deliberate: a prototype whose enums are
 * invented teaches the reviewer a vocabulary the product does not have, and the
 * screens then can't be built as drawn. Where a literal appears in a screen, it
 * is the literal the engine emits.
 *
 * ── The scenario ──
 *
 * One tenant, Northwind Systems, a Series-B SaaS company preparing for a SOC 2
 * Type II observation window of 2026-05-01 → 2026-07-31. Eight controls across
 * six families, six connected evidence sources, eight verification runs, and a
 * decision inbox with live disagreement in it. The numbers are internally
 * consistent — the queue count in the rail equals the open items on the queue
 * page, a control's verdict equals the aggregate of its checks, and the pack's
 * receipt list equals the receipts its runs issued — because a prototype whose
 * totals don't add up is the first thing a reviewer notices and the last thing
 * they trust.
 *
 * `NOW` is frozen so relative timestamps ("4h ago") never drift out of agreement
 * with the absolute ones, and so a screenshot taken today matches one taken next
 * week.
 */

/* ── Frozen clock ─────────────────────────────────────────────────────────── */

/** The scenario's "now". Everything relative is computed against this. */
export const NOW = new Date("2026-08-17T14:20:00Z");

/* ── Enums (mirrors of the real unions) ───────────────────────────────────── */

export type VerificationStatus = "PASS" | "FAIL" | "UNKNOWN" | "NOT_RUN";
export type Authority = "deterministic" | "semantic" | "human" | "imported";
export type Consequence = "low" | "medium" | "high" | "critical";
export type FindingStatus = "open" | "accepted_exception" | "remediated" | "dismissed";
export type ReviewCaseStatus =
  | "open"
  | "assigned"
  | "remediation_requested"
  | "exception_requested"
  | "exception_accepted"
  | "escalated"
  | "ready_for_reverification"
  | "closed";
export type ReviewDecisionKind =
  | "accept_exception"
  | "request_evidence"
  | "approve_remediation"
  | "reject_remediation"
  | "dismiss";
export type PolicyStatus = "draft" | "active" | "retired";
export type ActorRole =
  | "owner"
  | "operator"
  | "control_owner"
  | "reviewer"
  | "auditor"
  | "system";
export type SourceHealth = "healthy" | "stale" | "blocked" | "revoked" | "paused";
export type ConnectorProvider = "okta" | "workday" | "github" | "aws-iam" | "jira" | "vanta";
export type ConnectorFamily = "idp" | "hris" | "cloud" | "scm" | "ticketing" | "grc" | "manual";
export type RunStatus = "queued" | "running" | "completed" | "failed" | "blocked";
export type SemanticJudgmentKind = "support" | "conflict" | "unknown";
export type SemanticRoute =
  | "cited_support_needs_review"
  | "cited_conflict_exception"
  | "unknown_uncited"
  | "unknown_model_reported"
  | "high_consequence_support_needs_human"
  | "uncalibrated_model_needs_review";
export type CandidateStatus = "draft" | "needs-oracle" | "admitted" | "reject";
export type RuntimeMode = "cloud" | "private-vpc" | "local-offline";
export type MetricStatus = "pass" | "warn" | "fail" | "blocked";
export type EvidenceState = "active" | "redacted" | "deleted" | "expired";

/** The visual tone a status maps to. Keeps the palette decision in one place. */
export type Tone = "pass" | "fail" | "unknown" | "waived" | "running" | "accent" | "neutral";

/* ── Tenant & actors ─────────────────────────────────────────────────────── */

export interface Tenant {
  id: string;
  name: string;
  framework: string;
  periodStart: string;
  periodEnd: string;
  runtimeMode: RuntimeMode;
  keyCustody: "excellent-managed" | "customer-held" | "local-file";
}

/**
 * The two tenants are named consts BEFORE the array, and `TENANT` aliases one of
 * them rather than reading `TENANTS[0]`.
 *
 * This tsconfig has `noUncheckedIndexedAccess`, so `TENANTS[0]` is typed
 * `Tenant | undefined` and every one of the ~10 places that renders the tenant
 * name would need a non-null assertion. Naming the const gives the same data a
 * type that is honestly non-optional, with no assertions anywhere downstream.
 */
const NORTHWIND: Tenant = {
  id: "tnt_northwind",
  name: "Northwind Systems",
  framework: "SOC 2 Type II",
  periodStart: "2026-05-01",
  periodEnd: "2026-07-31",
  runtimeMode: "private-vpc",
  keyCustody: "customer-held",
};

const HALCYON: Tenant = {
  id: "tnt_halcyon",
  name: "Halcyon Health",
  framework: "HIPAA Security Rule",
  periodStart: "2026-04-01",
  periodEnd: "2026-09-30",
  runtimeMode: "local-offline",
  keyCustody: "local-file",
};

export const TENANTS: Tenant[] = [NORTHWIND, HALCYON];

/** The tenant every screen is scoped to. */
export const TENANT = NORTHWIND;

export interface Actor {
  id: string;
  name: string;
  role: ActorRole;
  initials: string;
  email: string;
}

/** The signed-in reviewer. Named for the same `noUncheckedIndexedAccess` reason. */
const REVIEWER: Actor = {
  id: "act_mw",
  name: "Matt Waite",
  role: "reviewer",
  initials: "MW",
  email: "matt@northwind.io",
};

/**
 * The fallback for an unresolvable actor id.
 *
 * `actor()` is called from render paths with ids that come out of fixture data, so
 * it must return a real `Actor` rather than `Actor | undefined` — otherwise every
 * `actor(x).name` needs a guard. Falling back to the SYSTEM actor is the honest
 * choice: an event with no resolvable human author was written by the engine.
 */
const SYSTEM: Actor = {
  id: "act_sys",
  name: "Verification engine",
  role: "system",
  initials: "VE",
  email: "system@excellent.so",
};

export const ACTORS: Actor[] = [
  REVIEWER,
  { id: "act_pd", name: "Priya Desai", role: "control_owner", initials: "PD", email: "priya@northwind.io" },
  { id: "act_jl", name: "Jordan Lee", role: "owner", initials: "JL", email: "jordan@northwind.io" },
  { id: "act_sk", name: "Sam Okonkwo", role: "operator", initials: "SO", email: "sam@northwind.io" },
  {
    id: "act_ext",
    name: "R. Alvarez (Brightline LLP)",
    role: "auditor",
    initials: "RA",
    email: "ralvarez@brightline.example",
  },
  SYSTEM,
];

export const ME = REVIEWER;

export function actor(id: string): Actor {
  return ACTORS.find((a) => a.id === id) ?? SYSTEM;
}

/* ── Controls & requirements ──────────────────────────────────────────────── */

export interface Requirement {
  id: string;
  controlId: string;
  text: string;
  evidenceKinds: string[];
}

export interface Control {
  id: string;
  ref: string;
  framework: string;
  family: string;
  title: string;
  ownerActorId: string;
  consequence: Consequence;
  status: VerificationStatus;
  /** Sources that feed this control's evidence. */
  sourceIds: string[];
  frequency: "continuous" | "daily" | "weekly" | "monthly" | "quarterly";
  populationSize: number;
  sampledSize: number;
}

export const CONTROLS: Control[] = [
  {
    id: "ctl_cc61",
    ref: "CC6.1",
    framework: "SOC 2",
    family: "Logical access",
    title: "Multi-factor authentication is enforced for all workforce accounts",
    ownerActorId: "act_pd",
    consequence: "critical",
    status: "FAIL",
    sourceIds: ["src_okta"],
    frequency: "continuous",
    populationSize: 412,
    sampledSize: 412,
  },
  {
    id: "ctl_cc62",
    ref: "CC6.2",
    framework: "SOC 2",
    family: "Logical access",
    title: "Access is approved by the resource owner before it is granted",
    ownerActorId: "act_pd",
    consequence: "high",
    status: "UNKNOWN",
    sourceIds: ["src_okta", "src_jira"],
    frequency: "weekly",
    populationSize: 68,
    sampledSize: 25,
  },
  {
    id: "ctl_cc63",
    ref: "CC6.3",
    framework: "SOC 2",
    family: "Logical access",
    title: "Access is revoked within 24 hours of termination",
    ownerActorId: "act_sk",
    consequence: "critical",
    status: "PASS",
    sourceIds: ["src_workday", "src_okta"],
    frequency: "continuous",
    populationSize: 19,
    sampledSize: 19,
  },
  {
    id: "ctl_cc66",
    ref: "CC6.6",
    framework: "SOC 2",
    family: "Boundary protection",
    title: "No cloud storage bucket or security group permits unrestricted public access",
    ownerActorId: "act_sk",
    consequence: "critical",
    status: "FAIL",
    sourceIds: ["src_aws"],
    frequency: "continuous",
    // 236 buckets + 88 security groups. The control tests both, so a population
    // of 236 understated what was covered by exactly the security-group count.
    populationSize: 324,
    sampledSize: 324,
  },
  {
    id: "ctl_cc72",
    ref: "CC7.2",
    framework: "SOC 2",
    family: "Monitoring",
    title: "Security events are reviewed and dispositioned within one business day",
    ownerActorId: "act_sk",
    consequence: "high",
    status: "UNKNOWN",
    sourceIds: ["src_vanta"],
    frequency: "daily",
    populationSize: 1_284,
    sampledSize: 40,
  },
  {
    id: "ctl_cc81",
    ref: "CC8.1",
    framework: "SOC 2",
    family: "Change management",
    title: "Every production change is peer-reviewed before it is merged",
    ownerActorId: "act_jl",
    consequence: "high",
    status: "PASS",
    sourceIds: ["src_github"],
    frequency: "continuous",
    populationSize: 1_907,
    sampledSize: 1_907,
  },
  {
    id: "ctl_cc14",
    ref: "CC1.4",
    framework: "SOC 2",
    family: "Control environment",
    title: "Background checks complete before a new hire receives production access",
    ownerActorId: "act_pd",
    consequence: "medium",
    status: "PASS",
    // Okta as well as Workday: the requirement compares the background-check date
    // against `idp.first_grant_at`, so the identity provider is load-bearing here.
    // `src_okta.controlIds` already claimed this control; this is the other half.
    sourceIds: ["src_workday", "src_okta"],
    frequency: "monthly",
    populationSize: 31,
    sampledSize: 31,
  },
  {
    id: "ctl_a12",
    ref: "A1.2",
    framework: "SOC 2",
    family: "Availability",
    title: "Backup restoration is tested at least quarterly and the result is recorded",
    ownerActorId: "act_jl",
    consequence: "medium",
    status: "NOT_RUN",
    sourceIds: [],
    frequency: "quarterly",
    populationSize: 4,
    sampledSize: 0,
  },
];

export const REQUIREMENTS: Requirement[] = [
  {
    id: "req_cc61_a",
    controlId: "ctl_cc61",
    text: "Every active workforce account in the identity provider has at least one enrolled MFA factor.",
    evidenceKinds: ["idp_user_export", "idp_factor_export"],
  },
  {
    id: "req_cc61_b",
    controlId: "ctl_cc61",
    text: "No authentication policy permits a password-only sign-in for a workforce application.",
    evidenceKinds: ["idp_policy_export"],
  },
  {
    id: "req_cc62_a",
    controlId: "ctl_cc62",
    text: "Each access grant in the period has an approval record dated at or before the grant timestamp.",
    evidenceKinds: ["idp_grant_log", "ticket_export"],
  },
  {
    id: "req_cc62_b",
    controlId: "ctl_cc62",
    text: "The approver is not the requester.",
    evidenceKinds: ["ticket_export"],
  },
  {
    id: "req_cc63_a",
    controlId: "ctl_cc63",
    text: "For each termination, the identity provider deactivation timestamp is within 24 hours of the HRIS termination date.",
    evidenceKinds: ["hris_termination_export", "idp_user_export"],
  },
  {
    id: "req_cc66_a",
    controlId: "ctl_cc66",
    text: "No object storage bucket policy grants read or write to a wildcard principal.",
    evidenceKinds: ["cloud_bucket_policy"],
  },
  {
    id: "req_cc66_b",
    controlId: "ctl_cc66",
    text: "No security group permits inbound 0.0.0.0/0 on a port other than 80 or 443.",
    evidenceKinds: ["cloud_sg_export"],
  },
  {
    id: "req_cc72_a",
    controlId: "ctl_cc72",
    text: "Each security alert raised in the period has a recorded disposition within one business day.",
    evidenceKinds: ["grc_alert_export"],
  },
  {
    id: "req_cc81_a",
    controlId: "ctl_cc81",
    text: "Every merge commit to a protected branch has at least one approving review from someone other than the author.",
    evidenceKinds: ["scm_pr_export"],
  },
  {
    id: "req_cc14_a",
    controlId: "ctl_cc14",
    text: "Each new hire's background check completion date precedes their production access grant date.",
    evidenceKinds: ["hris_onboarding_export", "idp_grant_log"],
  },
  {
    id: "req_a12_a",
    controlId: "ctl_a12",
    text: "A restore test result exists for each quarter in the observation window.",
    evidenceKinds: ["restore_test_record"],
  },
];

/** Every control id, in library order. Runs that sweep everything use this. */
export const ALL_CONTROL_IDS = CONTROLS.map((c) => c.id);

export function control(id: string): Control | undefined {
  return CONTROLS.find((c) => c.id === id);
}

export function requirementsFor(controlId: string): Requirement[] {
  return REQUIREMENTS.filter((r) => r.controlId === controlId);
}

/* ── Evidence sources (connectors) ────────────────────────────────────────── */

export interface Source {
  id: string;
  provider: ConnectorProvider;
  family: ConnectorFamily;
  displayName: string;
  health: SourceHealth;
  readOnly: true;
  grantedScopes: string[];
  requiredScopes: string[];
  cadenceMinutes: number;
  maxFreshnessMinutes: number;
  lastSuccessfulRunAt: string | null;
  nextRunAt: string | null;
  lastRunStatus: "succeeded" | "failed" | "retrying" | "running" | "revoked" | "never-run";
  artifactCount: number;
  controlIds: string[];
  note?: string;
}

export const SOURCES: Source[] = [
  {
    id: "src_okta",
    provider: "okta",
    family: "idp",
    displayName: "Okta — northwind.okta.com",
    health: "healthy",
    readOnly: true,
    grantedScopes: ["okta.users.read", "okta.policies.read", "okta.logs.read"],
    requiredScopes: ["okta.users.read", "okta.policies.read", "okta.logs.read"],
    cadenceMinutes: 360,
    maxFreshnessMinutes: 1_440,
    lastSuccessfulRunAt: "2026-08-17T12:02:00Z",
    nextRunAt: "2026-08-17T18:02:00Z",
    lastRunStatus: "succeeded",
    artifactCount: 148,
    controlIds: ["ctl_cc61", "ctl_cc62", "ctl_cc63", "ctl_cc14"],
  },
  {
    id: "src_workday",
    provider: "workday",
    family: "hris",
    displayName: "Workday — Northwind HR",
    health: "healthy",
    readOnly: true,
    grantedScopes: ["workday.workers.read", "workday.terminations.read"],
    requiredScopes: ["workday.workers.read", "workday.terminations.read"],
    cadenceMinutes: 1_440,
    maxFreshnessMinutes: 2_880,
    lastSuccessfulRunAt: "2026-08-17T06:00:00Z",
    nextRunAt: "2026-08-18T06:00:00Z",
    lastRunStatus: "succeeded",
    artifactCount: 62,
    controlIds: ["ctl_cc63", "ctl_cc14"],
  },
  {
    id: "src_github",
    provider: "github",
    family: "scm",
    displayName: "GitHub — northwind-systems org",
    health: "healthy",
    readOnly: true,
    grantedScopes: ["repo:read", "org:read"],
    requiredScopes: ["repo:read", "org:read"],
    cadenceMinutes: 240,
    maxFreshnessMinutes: 720,
    lastSuccessfulRunAt: "2026-08-17T13:40:00Z",
    nextRunAt: "2026-08-17T17:40:00Z",
    lastRunStatus: "succeeded",
    artifactCount: 311,
    controlIds: ["ctl_cc81"],
  },
  {
    id: "src_aws",
    provider: "aws-iam",
    family: "cloud",
    displayName: "AWS — northwind-prod",
    health: "healthy",
    readOnly: true,
    grantedScopes: ["iam:Get*", "iam:List*", "s3:GetBucketPolicy", "ec2:DescribeSecurityGroups"],
    requiredScopes: ["iam:Get*", "iam:List*", "s3:GetBucketPolicy", "ec2:DescribeSecurityGroups"],
    cadenceMinutes: 180,
    maxFreshnessMinutes: 480,
    lastSuccessfulRunAt: "2026-08-17T13:10:00Z",
    nextRunAt: "2026-08-17T16:10:00Z",
    lastRunStatus: "succeeded",
    artifactCount: 204,
    controlIds: ["ctl_cc66"],
  },
  {
    id: "src_jira",
    provider: "jira",
    family: "ticketing",
    displayName: "Jira — NW Access Requests",
    health: "stale",
    readOnly: true,
    grantedScopes: ["read:jira-work"],
    requiredScopes: ["read:jira-work", "read:jira-user"],
    cadenceMinutes: 720,
    maxFreshnessMinutes: 1_440,
    lastSuccessfulRunAt: "2026-08-15T09:15:00Z",
    nextRunAt: "2026-08-17T15:00:00Z",
    lastRunStatus: "retrying",
    artifactCount: 41,
    controlIds: ["ctl_cc62"],
    note:
      "Missing scope read:jira-user, so approver identity cannot be resolved. 43 of 68 access grants in the period have no resolvable approver — this is why CC6.2 is UNKNOWN rather than PASS.",
  },
  {
    id: "src_vanta",
    provider: "vanta",
    family: "grc",
    displayName: "Vanta — Northwind workspace",
    health: "blocked",
    readOnly: true,
    grantedScopes: [],
    requiredScopes: ["vanta.controls.read", "vanta.evidence.read"],
    cadenceMinutes: 1_440,
    maxFreshnessMinutes: 2_880,
    lastSuccessfulRunAt: "2026-08-09T04:00:00Z",
    nextRunAt: null,
    lastRunStatus: "failed",
    artifactCount: 18,
    controlIds: ["ctl_cc72"],
    note:
      "The API token was revoked on 2026-08-11 during a credential rotation and has not been replaced. Evidence for CC7.2 is 8 days stale against a 2-day freshness SLA.",
  },
];

export function source(id: string): Source | undefined {
  return SOURCES.find((s) => s.id === id);
}

/** Display label for a provider — used in the connector catalogue and tables. */
export const PROVIDER_LABEL: Record<ConnectorProvider, string> = {
  okta: "Okta",
  workday: "Workday",
  github: "GitHub",
  "aws-iam": "AWS IAM",
  jira: "Jira",
  vanta: "Vanta",
};

/* ── Evidence artifacts & observations ────────────────────────────────────── */

export interface EvidenceArtifact {
  id: string;
  sourceId: string;
  sourceKind: string;
  label: string;
  contentHash: string;
  collectedAt: string;
  collectorActorId: string;
  state: EvidenceState;
  encryptedAtRest: true;
  sizeBytes: number;
  observationCount: number;
  controlIds: string[];
  /** Which receipts pin this artifact's hash. DERIVED — see `ARTIFACTS` below. */
  citedByReceiptIds: string[];
}

/** The authored half. `citedByReceiptIds` is computed, so it is absent here. */
const ARTIFACT_FIXTURES: Omit<EvidenceArtifact, "citedByReceiptIds">[] = [
  {
    id: "art_okta_users_0817",
    sourceId: "src_okta",
    sourceKind: "idp_user_export",
    label: "Okta active user + factor enrolment export",
    contentHash: "sha256:9f2c41ab7d0e5583c1b9a4f6e8d2071c3a5b8e94f16d2c7a08b3e5d91f4c6a27",
    collectedAt: "2026-08-17T12:02:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 284_912,
    observationCount: 412,
    controlIds: ["ctl_cc61", "ctl_cc63"],
  },
  {
    id: "art_okta_policy_0817",
    sourceId: "src_okta",
    sourceKind: "idp_policy_export",
    label: "Okta authentication policy set",
    contentHash: "sha256:3e77b0c9d4a21f68e5c07b3a9d81624f7e0a5c38b92d6f14a7c3e08b5d29f461",
    collectedAt: "2026-08-17T12:02:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 41_608,
    observationCount: 14,
    controlIds: ["ctl_cc61"],
  },
  {
    id: "art_aws_bucket_0817",
    sourceId: "src_aws",
    sourceKind: "cloud_bucket_policy",
    label: "S3 bucket policy snapshot (236 buckets)",
    contentHash: "sha256:c18d5a06f3b7e429d0c85b1a7f6e3d924c0b58e7a31d6f2c9b04e8a5d17f3c62",
    collectedAt: "2026-08-17T13:10:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 512_440,
    observationCount: 236,
    controlIds: ["ctl_cc66"],
  },
  {
    id: "art_aws_sg_0817",
    sourceId: "src_aws",
    sourceKind: "cloud_sg_export",
    label: "EC2 security group rule export",
    contentHash: "sha256:70a4e91c5d8b26f03a7c14e9b5d802f6c39a7e05b8d14c6f2a90b3e7d5c81a49",
    collectedAt: "2026-08-17T13:10:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 96_204,
    observationCount: 88,
    controlIds: ["ctl_cc66"],
  },
  {
    id: "art_workday_term_0817",
    sourceId: "src_workday",
    sourceKind: "hris_termination_export",
    label: "Workday termination register (period)",
    contentHash: "sha256:5b3c8e07a19d4f62c0b7a5e83d16f429c7b0e5a38d92f61c4a08b7e3d5c19f02",
    collectedAt: "2026-08-17T06:00:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 18_772,
    observationCount: 19,
    controlIds: ["ctl_cc63"],
  },
  {
    id: "art_github_pr_0817",
    sourceId: "src_github",
    sourceKind: "scm_pr_export",
    label: "Merged pull request + review export",
    contentHash: "sha256:a7e04c93b5d81f26e0a3c7b94d5f812a6c0b39e7a58d1f4c6b23e0a9d78c5b31",
    collectedAt: "2026-08-17T13:40:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 1_884_310,
    observationCount: 1_907,
    controlIds: ["ctl_cc81"],
  },
  {
    id: "art_jira_grants_0815",
    sourceId: "src_jira",
    sourceKind: "ticket_export",
    label: "Jira access-request tickets (partial — scope-limited)",
    contentHash: "sha256:e93a1c7b05d84f62a0c37b5e91d6f428c7a0b53e8d29f14c6b30a8e7d5c92f16",
    collectedAt: "2026-08-15T09:15:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 63_118,
    observationCount: 25,
    controlIds: ["ctl_cc62"],
  },
  {
    id: "art_vanta_alerts_0809",
    sourceId: "src_vanta",
    sourceKind: "grc_alert_export",
    label: "Vanta security alert register (stale)",
    contentHash: "sha256:1d6b4a09e7c35f82b0a49c7e35d1f6a28c0b74e9a53d81f6c2b40e9a7d38c15b",
    collectedAt: "2026-08-09T04:00:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 227_004,
    observationCount: 40,
    controlIds: ["ctl_cc72"],
  },
  {
    id: "art_okta_users_0731",
    sourceId: "src_okta",
    sourceKind: "idp_user_export",
    label: "Okta user + factor export (period close snapshot)",
    contentHash: "sha256:44b9e01c7a5d83f26b0c48e9a37d1f5c62a0b83e7d91f4c62a08b5e3d7c1a940",
    collectedAt: "2026-07-31T23:59:00Z",
    collectorActorId: "act_sys",
    state: "active",
    encryptedAtRest: true,
    sizeBytes: 279_884,
    observationCount: 407,
    controlIds: ["ctl_cc61", "ctl_cc63"],
  },
  {
    id: "art_hris_bgc_0731",
    sourceId: "src_workday",
    sourceKind: "hris_onboarding_export",
    label: "Background check completion register",
    contentHash: "sha256:8c05a3e91b7d46f2c0a85b3e97d14f6a2c8b05e39d7a1f4c6b20e8a5d93c17f4",
    collectedAt: "2026-07-31T23:59:00Z",
    collectorActorId: "act_sys",
    state: "redacted",
    encryptedAtRest: true,
    sizeBytes: 12_440,
    observationCount: 31,
    controlIds: ["ctl_cc14"],
  },
];

export function artifact(id: string): EvidenceArtifact | undefined {
  return ARTIFACTS.find((a) => a.id === id);
}

/** A derived fact pulled out of an artifact — the unit a check actually reads. */
export interface Observation {
  id: string;
  artifactId: string;
  subjectRef: string;
  predicate: string;
  value: string;
  extractorVersion: string;
  observedAt: string;
}

export const OBSERVATIONS: Observation[] = [
  {
    id: "obs_1",
    artifactId: "art_okta_users_0817",
    subjectRef: "okta:user/00u4a91zzQ",
    predicate: "mfa_factor_count",
    value: "0",
    extractorVersion: "okta-extractor@2.4.1",
    observedAt: "2026-08-17T12:02:04Z",
  },
  {
    id: "obs_2",
    artifactId: "art_okta_users_0817",
    subjectRef: "okta:user/00u4a91zzQ",
    predicate: "status",
    value: "ACTIVE",
    extractorVersion: "okta-extractor@2.4.1",
    observedAt: "2026-08-17T12:02:04Z",
  },
  {
    id: "obs_3",
    artifactId: "art_okta_users_0817",
    subjectRef: "okta:user/00u7c22kkR",
    predicate: "mfa_factor_count",
    value: "0",
    extractorVersion: "okta-extractor@2.4.1",
    observedAt: "2026-08-17T12:02:04Z",
  },
  {
    id: "obs_4",
    artifactId: "art_aws_bucket_0817",
    subjectRef: "aws:s3/northwind-analytics-scratch",
    predicate: "wildcard_principal_grant",
    value: "s3:GetObject",
    extractorVersion: "aws-extractor@3.1.0",
    observedAt: "2026-08-17T13:10:12Z",
  },
  {
    id: "obs_5",
    artifactId: "art_aws_sg_0817",
    subjectRef: "aws:sg/sg-0b41e77a9",
    predicate: "inbound_any_ipv4_port",
    value: "5432",
    extractorVersion: "aws-extractor@3.1.0",
    observedAt: "2026-08-17T13:10:12Z",
  },
];

export function observationsFor(artifactId: string): Observation[] {
  return OBSERVATIONS.filter((o) => o.artifactId === artifactId);
}

/* ── Policies ─────────────────────────────────────────────────────────────── */

export interface Policy {
  id: string;
  controlId: string;
  version: number;
  status: PolicyStatus;
  evaluationMode: "all_required_pass" | "any_required_pass";
  requiredRequirementIds: string[];
  activatedAt: string | null;
  activatedByActorId: string | null;
  createdByActorId: string;
  createdAt: string;
  /** Whether this version tightens, loosens, or leaves the bar unchanged. */
  intent: "tighten" | "neutral" | "loosen";
  /** Human-readable expression shown in the policy editor. */
  expression: string;
  frozenCaseCount: number;
  frozenCasesPassing: number;
  /** Set on a draft that cannot activate yet, with the reason. */
  blockedReason?: string;
}

export const POLICIES: Policy[] = [
  {
    id: "pol_cc61_v3",
    controlId: "ctl_cc61",
    version: 3,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc61_a", "req_cc61_b"],
    activatedAt: "2026-06-02T15:40:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_pd",
    createdAt: "2026-05-30T11:12:00Z",
    intent: "tighten",
    expression: `all(
  every(idp.users.where(status == "ACTIVE"), u => u.mfa_factor_count >= 1),
  none(idp.policies, p => p.allows_password_only == true)
)`,
    frozenCaseCount: 24,
    frozenCasesPassing: 24,
  },
  {
    id: "pol_cc61_v4",
    controlId: "ctl_cc61",
    version: 4,
    status: "draft",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc61_a"],
    activatedAt: null,
    activatedByActorId: null,
    createdByActorId: "act_pd",
    createdAt: "2026-08-16T17:04:00Z",
    intent: "loosen",
    expression: `all(
  every(idp.users.where(status == "ACTIVE" and !u.service_account), u => u.mfa_factor_count >= 1)
)`,
    frozenCaseCount: 24,
    frozenCasesPassing: 21,
    blockedReason:
      "loosening-converts-frozen-non-pass-to-pass — the service-account exclusion flips 3 frozen regression cases from FAIL to PASS. Loosening a policy needs an impact analysis and activation by someone other than its author.",
  },
  {
    id: "pol_cc63_v2",
    controlId: "ctl_cc63",
    version: 2,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc63_a"],
    activatedAt: "2026-05-14T09:20:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_sk",
    createdAt: "2026-05-13T16:44:00Z",
    intent: "neutral",
    expression: `every(hris.terminations, t =>
  idp.deactivation_at(t.worker_id) - t.terminated_at <= hours(24)
)`,
    frozenCaseCount: 12,
    frozenCasesPassing: 12,
  },
  {
    id: "pol_cc66_v5",
    controlId: "ctl_cc66",
    version: 5,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc66_a", "req_cc66_b"],
    activatedAt: "2026-07-08T13:05:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_sk",
    createdAt: "2026-07-07T10:30:00Z",
    intent: "tighten",
    expression: `all(
  none(cloud.buckets, b => b.wildcard_principal_grant != null),
  none(cloud.security_groups, sg =>
    sg.inbound_any_ipv4_port not in [80, 443])
)`,
    frozenCaseCount: 31,
    frozenCasesPassing: 31,
  },
  {
    id: "pol_cc81_v2",
    controlId: "ctl_cc81",
    version: 2,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc81_a"],
    activatedAt: "2026-05-06T08:00:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_jl",
    createdAt: "2026-05-05T19:11:00Z",
    intent: "neutral",
    expression: `every(scm.merges.where(branch in protected_branches), m =>
  count(m.approvals.where(reviewer != m.author)) >= 1
)`,
    frozenCaseCount: 18,
    frozenCasesPassing: 18,
  },
  {
    id: "pol_cc62_v1",
    controlId: "ctl_cc62",
    version: 1,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc62_a", "req_cc62_b"],
    activatedAt: "2026-05-06T08:00:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_pd",
    createdAt: "2026-05-04T14:00:00Z",
    intent: "neutral",
    expression: `every(idp.grants, g =>
  exists(tickets.approvals(g.request_id), a =>
    a.approved_at <= g.granted_at and a.approver != g.requester)
)`,
    frozenCaseCount: 9,
    frozenCasesPassing: 9,
  },
  {
    id: "pol_cc72_v1",
    controlId: "ctl_cc72",
    version: 1,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc72_a"],
    activatedAt: "2026-05-06T08:00:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_sk",
    createdAt: "2026-05-05T12:00:00Z",
    intent: "neutral",
    expression: `every(grc.alerts, a =>
  a.disposition != null and
  business_hours_between(a.raised_at, a.dispositioned_at) <= hours(8)
)`,
    frozenCaseCount: 6,
    frozenCasesPassing: 6,
  },
  {
    id: "pol_cc14_v1",
    controlId: "ctl_cc14",
    version: 1,
    status: "active",
    evaluationMode: "all_required_pass",
    requiredRequirementIds: ["req_cc14_a"],
    activatedAt: "2026-05-06T08:00:00Z",
    activatedByActorId: "act_jl",
    createdByActorId: "act_pd",
    createdAt: "2026-05-05T12:00:00Z",
    intent: "neutral",
    expression: `every(hris.new_hires, h =>
  h.background_check_completed_at <= idp.first_grant_at(h.worker_id)
)`,
    frozenCaseCount: 7,
    frozenCasesPassing: 7,
  },
];

export function policy(id: string): Policy | undefined {
  return POLICIES.find((p) => p.id === id);
}


/* ── Runs & check results ─────────────────────────────────────────────────── */

export interface CheckResult {
  id: string;
  runId: string;
  policyId: string;
  requirementId: string;
  controlId: string;
  status: VerificationStatus;
  authority: Authority;
  observationIds: string[];
  evidenceArtifactIds: string[];
  citationRefs: string[];
  reason: string;
  durationMs: number;
}

export interface Run {
  id: string;
  label: string;
  status: RunStatus;
  aggregate: VerificationStatus;
  controlIds: string[];
  startedAt: string;
  completedAt: string | null;
  createdByActorId: string;
  trigger: "schedule" | "manual" | "connector_refresh" | "api";
  receiptId: string | null;
  engineVersion: string;
  evidenceArtifactIds: string[];
}

/**
 * Checks for the CURRENT full sweep, written out because their `reason` strings
 * are the copy the UI shows a reviewer.
 *
 * The per-control continuous runs and the two period-close sweeps get their
 * checks from `historicalChecks()` below rather than being hand-written: seven of
 * the eight runs used to carry NO checks while their receipts claimed 1–11, so
 * every run page but this one rendered an empty "Check results" section under a
 * receipt card asserting a number. Deriving them from the same requirement list
 * the control declares makes that class of drift impossible.
 */
const SWEEP_CHECKS: CheckResult[] = [
  /* Run 8818 — the in-flight full sweep */
  {
    id: "chk_8818_1",
    runId: "run_8818",
    policyId: "pol_cc61_v3",
    requirementId: "req_cc61_a",
    controlId: "ctl_cc61",
    status: "FAIL",
    authority: "deterministic",
    observationIds: ["obs_1", "obs_2", "obs_3"],
    evidenceArtifactIds: ["art_okta_users_0817"],
    citationRefs: ["art_okta_users_0817#row/00u4a91zzQ", "art_okta_users_0817#row/00u7c22kkR"],
    reason:
      "2 of 412 active accounts have mfa_factor_count = 0: okta:user/00u4a91zzQ (svc-etl-runner), okta:user/00u7c22kkR (contractor, d.mehta).",
    durationMs: 412,
  },
  {
    id: "chk_8818_2",
    runId: "run_8818",
    policyId: "pol_cc61_v3",
    requirementId: "req_cc61_b",
    controlId: "ctl_cc61",
    status: "PASS",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: ["art_okta_policy_0817"],
    citationRefs: ["art_okta_policy_0817#policies"],
    reason: "No authentication policy in the export permits password-only sign-in (14 policies evaluated).",
    durationMs: 96,
  },
  {
    id: "chk_8818_3",
    runId: "run_8818",
    policyId: "pol_cc62_v1",
    requirementId: "req_cc62_a",
    controlId: "ctl_cc62",
    status: "UNKNOWN",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: ["art_jira_grants_0815"],
    citationRefs: ["art_jira_grants_0815#tickets"],
    reason:
      "43 of 68 access grants have no resolvable approval record. The Jira connection is missing the read:jira-user scope, so approver identity cannot be read. Insufficient evidence to decide — not a failure.",
    durationMs: 188,
  },
  {
    id: "chk_8818_4",
    runId: "run_8818",
    policyId: "pol_cc62_v1",
    requirementId: "req_cc62_b",
    controlId: "ctl_cc62",
    status: "UNKNOWN",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: ["art_jira_grants_0815"],
    citationRefs: [],
    reason: "Cannot evaluate approver ≠ requester without approver identity. Blocked on the same missing scope.",
    durationMs: 44,
  },
  {
    id: "chk_8818_5",
    runId: "run_8818",
    policyId: "pol_cc63_v2",
    requirementId: "req_cc63_a",
    controlId: "ctl_cc63",
    status: "PASS",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: ["art_workday_term_0817", "art_okta_users_0817"],
    citationRefs: ["art_workday_term_0817#terminations", "art_okta_users_0817#deactivations"],
    reason:
      "All 19 terminations in the period were deprovisioned within 24h. Slowest: 19h 40m (worker 4471, 2026-06-18).",
    durationMs: 604,
  },
  {
    id: "chk_8818_6",
    runId: "run_8818",
    policyId: "pol_cc66_v5",
    requirementId: "req_cc66_a",
    controlId: "ctl_cc66",
    status: "FAIL",
    authority: "deterministic",
    observationIds: ["obs_4"],
    evidenceArtifactIds: ["art_aws_bucket_0817"],
    citationRefs: ["art_aws_bucket_0817#bucket/northwind-analytics-scratch"],
    reason:
      "Bucket northwind-analytics-scratch grants s3:GetObject to Principal \"*\". 1 of 236 buckets affected.",
    durationMs: 328,
  },
  {
    id: "chk_8818_7",
    runId: "run_8818",
    policyId: "pol_cc66_v5",
    requirementId: "req_cc66_b",
    controlId: "ctl_cc66",
    status: "FAIL",
    authority: "deterministic",
    observationIds: ["obs_5"],
    evidenceArtifactIds: ["art_aws_sg_0817"],
    citationRefs: ["art_aws_sg_0817#sg/sg-0b41e77a9"],
    reason:
      "Security group sg-0b41e77a9 (rds-legacy-access) permits inbound 0.0.0.0/0 on port 5432. 1 of 88 groups affected.",
    durationMs: 142,
  },
  {
    id: "chk_8818_8",
    runId: "run_8818",
    policyId: "pol_cc72_v1",
    requirementId: "req_cc72_a",
    controlId: "ctl_cc72",
    status: "UNKNOWN",
    authority: "semantic",
    observationIds: [],
    evidenceArtifactIds: ["art_vanta_alerts_0809"],
    citationRefs: ["art_vanta_alerts_0809#alert/AL-2291"],
    reason:
      "Evidence is 8 days old against a 2-day freshness SLA (source blocked since 2026-08-11). The semantic worker read 40 sampled dispositions and returned \"support\" with citations, but a semantic judgment can never issue an authoritative PASS — routed to human review.",
    durationMs: 8_940,
  },
  {
    id: "chk_8818_9",
    runId: "run_8818",
    policyId: "pol_cc81_v2",
    requirementId: "req_cc81_a",
    controlId: "ctl_cc81",
    status: "PASS",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: ["art_github_pr_0817"],
    citationRefs: ["art_github_pr_0817#merges"],
    reason:
      "1,907 of 1,907 merges to protected branches carry an approving review from a non-author.",
    durationMs: 2_204,
  },
  {
    id: "chk_8818_10",
    runId: "run_8818",
    policyId: "pol_cc14_v1",
    requirementId: "req_cc14_a",
    controlId: "ctl_cc14",
    status: "PASS",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: ["art_hris_bgc_0731"],
    citationRefs: ["art_hris_bgc_0731#hires"],
    reason: "All 31 new hires completed a background check before their first production grant.",
    durationMs: 210,
  },
  {
    id: "chk_8818_11",
    runId: "run_8818",
    // A1.2 has no active policy — nothing is mapped to it, which is the finding.
    // It previously named CC6.1's MFA policy, which would have rendered an
    // `every(idp.users …)` expression as A1.2's rule.
    policyId: "",
    requirementId: "req_a12_a",
    controlId: "ctl_a12",
    // NOT_RUN, matching `ctl_a12.status`. Nothing is mapped, so no check
    // executed — and because `Finding.verificationStatus` accepts only
    // FAIL | UNKNOWN, this deliberately opens NO finding. An unmapped control is
    // SETUP work, not review work: it surfaces on Overview as a pack blocker, on
    // the Controls table as NOT RUN, and on the Scorecard as lost coverage —
    // none of which is a decision a reviewer can make.
    status: "NOT_RUN",
    authority: "deterministic",
    observationIds: [],
    evidenceArtifactIds: [],
    citationRefs: [],
    reason:
      "No evidence source is mapped to A1.2. Restore-test records are filed manually and have not been imported.",
    durationMs: 0,
  },
];

/**
 * Build a run's checks from its controls' requirements.
 *
 * `overrides` names the requirements that did NOT pass on that run, with the
 * status and the sentence to show. Everything else passes. This keeps a
 * historical run's check list in lockstep with the requirement list its controls
 * actually declare — add a requirement and every run gains a check for it.
 */
function historicalChecks(
  runId: string,
  controlIds: string[],
  overrides: Record<string, { status: VerificationStatus; authority?: Authority; reason: string }> = {},
): CheckResult[] {
  const out: CheckResult[] = [];
  let n = 0;
  for (const controlId of controlIds) {
    const pol = POLICIES.find((p) => p.controlId === controlId && p.status === "active");
    for (const req of REQUIREMENTS.filter((r) => r.controlId === controlId)) {
      n += 1;
      const over = overrides[req.id];
      const c = control(controlId);
      out.push({
        id: `chk_${runId.replace("run_", "")}_${n}`,
        runId,
        policyId: pol?.id ?? "",
        requirementId: req.id,
        controlId,
        status: over?.status ?? "PASS",
        authority: over?.authority ?? "deterministic",
        observationIds: [],
        // `ARTIFACT_FIXTURES`, not `ARTIFACTS`: this runs during module
        // evaluation, before the derived list exists. Only `controlIds` is
        // needed here, which the fixtures already carry.
        evidenceArtifactIds: ARTIFACT_FIXTURES.filter((a) => a.controlIds.includes(controlId)).map((a) => a.id),
        citationRefs: [],
        reason:
          over?.reason ??
          `Evaluated against the full population for ${c?.ref ?? controlId}; no exception found.`,
        durationMs: 120 + n * 17,
      });
    }
  }
  return out;
}

/** Every check across every run. `checksFor` and `checksForControl` read this. */
export const CHECKS: CheckResult[] = [
  ...SWEEP_CHECKS,
  ...historicalChecks("run_8817", ["ctl_cc81"]),
  ...historicalChecks("run_8816", ["ctl_cc66"], {
    req_cc66_a: {
      status: "FAIL",
      reason:
        'Bucket northwind-analytics-scratch grants s3:GetObject to Principal "*". 1 of 236 buckets affected.',
    },
    req_cc66_b: {
      status: "FAIL",
      reason:
        "Security group sg-0b41e77a9 (rds-legacy-access) permits inbound 0.0.0.0/0 on port 5432. 1 of 88 groups affected.",
    },
  }),
  ...historicalChecks("run_8815", ["ctl_cc63"]),
  ...historicalChecks("run_8814", ["ctl_cc61"], {
    req_cc61_a: {
      status: "FAIL",
      reason:
        "2 of 412 active accounts have mfa_factor_count = 0: okta:user/00u4a91zzQ (svc-etl-runner), okta:user/00u7c22kkR (contractor, d.mehta).",
    },
  }),
  ...historicalChecks("run_8813", ["ctl_cc72"], {
    req_cc72_a: {
      status: "UNKNOWN",
      reason:
        "Source blocked since 2026-08-11 — the newest alert register is 8 days old against a 2-day freshness SLA. The run could not read current evidence and issued no receipt.",
    },
  }),
  // The two period-close sweeps cover every control, which is what makes their
  // receipts' 11-check count true rather than asserted.
  ...historicalChecks("run_8812", ALL_CONTROL_IDS, {
    req_cc61_a: {
      status: "FAIL",
      reason: "3 of 407 active accounts had no enrolled factor at period close.",
    },
    req_cc63_a: {
      status: "FAIL",
      reason:
        "Worker 4318 was terminated 2026-07-28 02:00 and deactivated 2026-07-30 15:00 — 61 hours, against a 24-hour requirement.",
    },
    req_cc62_a: { status: "UNKNOWN", reason: "Approver identity unresolvable — Jira scope missing." },
    req_cc62_b: { status: "UNKNOWN", reason: "Blocked on the same missing scope." },
    req_cc72_a: { status: "UNKNOWN", reason: "Monitoring evidence stale at period close." },
    req_a12_a: { status: "NOT_RUN", reason: "No evidence source mapped to A1.2." },
  }),
  ...historicalChecks("run_8811", ALL_CONTROL_IDS, {
    req_cc61_a: { status: "FAIL", reason: "5 of 401 active accounts had no enrolled factor." },
    req_cc14_a: {
      status: "UNKNOWN",
      reason:
        "Workers 2871 and 2903 joined via the Cedar acquisition and pre-date the background-check programme; no record exists to collect.",
    },
    req_cc62_a: { status: "UNKNOWN", reason: "Approver identity unresolvable — Jira scope missing." },
    req_cc62_b: { status: "UNKNOWN", reason: "Blocked on the same missing scope." },
    req_cc72_a: { status: "UNKNOWN", reason: "Monitoring evidence stale." },
    req_a12_a: { status: "NOT_RUN", reason: "No evidence source mapped to A1.2." },
  }),
];

/**
 * Runs.
 *
 * No `checkIds` field: it was hand-written, empty for seven of the eight runs,
 * and rendered nowhere — a field that can only be wrong. `checksFor(runId)` is
 * the one way to ask which checks a run produced.
 */
export const RUNS: Run[] = [
  {
    id: "run_8818",
    label: "Full control sweep — August interim",
    status: "completed",
    aggregate: "FAIL",
    controlIds: CONTROLS.map((c) => c.id),
    startedAt: "2026-08-17T13:48:00Z",
    completedAt: "2026-08-17T13:51:12Z",
    createdByActorId: "act_mw",
    trigger: "manual",
    receiptId: "rcp_8818",
    engineVersion: "verifier@0.31.2",
    evidenceArtifactIds: [
      "art_okta_users_0817",
      "art_okta_policy_0817",
      "art_aws_bucket_0817",
      "art_aws_sg_0817",
      "art_workday_term_0817",
      "art_github_pr_0817",
      "art_jira_grants_0815",
      "art_vanta_alerts_0809",
      "art_hris_bgc_0731",
    ],
  },
  {
    id: "run_8817",
    label: "CC8.1 change-management — continuous",
    status: "completed",
    aggregate: "PASS",
    controlIds: ["ctl_cc81"],
    startedAt: "2026-08-17T13:41:00Z",
    completedAt: "2026-08-17T13:41:44Z",
    createdByActorId: "act_sys",
    trigger: "connector_refresh",
    receiptId: "rcp_8817",
    engineVersion: "verifier@0.31.2",
    evidenceArtifactIds: ["art_github_pr_0817"],
  },
  {
    id: "run_8816",
    label: "CC6.6 boundary protection — continuous",
    status: "completed",
    aggregate: "FAIL",
    controlIds: ["ctl_cc66"],
    startedAt: "2026-08-17T13:11:00Z",
    completedAt: "2026-08-17T13:11:38Z",
    createdByActorId: "act_sys",
    trigger: "connector_refresh",
    receiptId: "rcp_8816",
    engineVersion: "verifier@0.31.2",
    evidenceArtifactIds: ["art_aws_bucket_0817", "art_aws_sg_0817"],
  },
  {
    id: "run_8815",
    label: "CC6.3 revocation timeliness — continuous",
    status: "completed",
    aggregate: "PASS",
    controlIds: ["ctl_cc63"],
    startedAt: "2026-08-17T06:04:00Z",
    completedAt: "2026-08-17T06:04:51Z",
    createdByActorId: "act_sys",
    trigger: "connector_refresh",
    receiptId: "rcp_8815",
    engineVersion: "verifier@0.31.2",
    evidenceArtifactIds: ["art_workday_term_0817", "art_okta_users_0817"],
  },
  {
    id: "run_8814",
    label: "CC6.1 MFA enforcement — continuous",
    status: "completed",
    aggregate: "FAIL",
    controlIds: ["ctl_cc61"],
    startedAt: "2026-08-17T12:03:00Z",
    completedAt: "2026-08-17T12:03:29Z",
    createdByActorId: "act_sys",
    trigger: "connector_refresh",
    receiptId: "rcp_8814",
    engineVersion: "verifier@0.31.2",
    evidenceArtifactIds: ["art_okta_users_0817", "art_okta_policy_0817"],
  },
  {
    id: "run_8813",
    label: "CC7.2 monitoring — daily",
    status: "blocked",
    aggregate: "UNKNOWN",
    controlIds: ["ctl_cc72"],
    startedAt: "2026-08-17T04:00:00Z",
    completedAt: "2026-08-17T04:00:06Z",
    createdByActorId: "act_sys",
    trigger: "schedule",
    receiptId: null,
    engineVersion: "verifier@0.31.2",
    evidenceArtifactIds: ["art_vanta_alerts_0809"],
  },
  {
    id: "run_8812",
    label: "Period-close sweep — July",
    status: "completed",
    aggregate: "FAIL",
    controlIds: CONTROLS.map((c) => c.id),
    startedAt: "2026-08-01T02:00:00Z",
    completedAt: "2026-08-01T02:04:41Z",
    createdByActorId: "act_sys",
    trigger: "schedule",
    receiptId: "rcp_8812",
    engineVersion: "verifier@0.30.7",
    evidenceArtifactIds: ["art_okta_users_0731", "art_hris_bgc_0731"],
  },
  {
    id: "run_8811",
    label: "Period-close sweep — June",
    status: "completed",
    aggregate: "FAIL",
    controlIds: CONTROLS.map((c) => c.id),
    startedAt: "2026-07-01T02:00:00Z",
    completedAt: "2026-07-01T02:03:58Z",
    createdByActorId: "act_sys",
    trigger: "schedule",
    receiptId: "rcp_8811",
    engineVersion: "verifier@0.29.4",
    evidenceArtifactIds: ["art_okta_users_0731"],
  },
];

export function run(id: string): Run | undefined {
  return RUNS.find((r) => r.id === id);
}

export function checksFor(runId: string): CheckResult[] {
  return CHECKS.filter((c) => c.runId === runId);
}

/** Checks for a control, newest run first. Used on the control detail page. */
export function checksForControl(controlId: string): CheckResult[] {
  return CHECKS.filter((c) => c.controlId === controlId);
}

/* ── Findings & review ────────────────────────────────────────────────────── */

export interface DecisionEvent {
  id: string;
  at: string;
  actorId: string;
  kind:
    | "run_completed"
    | "finding_opened"
    | "assigned"
    | "comment"
    | "request_evidence"
    | "remediation_requested"
    | "exception_requested"
    | "exception_accepted"
    | "approve_remediation"
    | "reject_remediation"
    | "escalated"
    | "ready_for_reverification"
    | "reverified"
    | "receipt_issued"
    | "dismiss";
  title: string;
  note?: string;
}

export interface Finding {
  id: string;
  ref: string;
  runId: string;
  controlId: string;
  requirementId: string;
  checkId: string;
  /** FAIL or UNKNOWN — a PASS never opens a finding. */
  verificationStatus: "FAIL" | "UNKNOWN";
  status: FindingStatus;
  caseStatus: ReviewCaseStatus;
  severity: Consequence;
  ownerActorId: string;
  assignedActorId: string | null;
  openedAt: string;
  dueAt: string | null;
  title: string;
  summary: string;
  /** What the reviewer is being asked to decide, in one sentence. */
  ask: string;
  evidenceArtifactIds: string[];
  citationRefs: string[];
  authority: Authority;
  timeline: DecisionEvent[];
  /** Set when a semantic worker touched this finding. */
  semanticRoute?: SemanticRoute;
}

export const FINDINGS: Finding[] = [
  {
    id: "fnd_4401",
    ref: "F-4401",
    // run_8814, whose completedAt IS this finding's openedAt and which its own
    // first timeline entry names.
    runId: "run_8814",
    controlId: "ctl_cc61",
    requirementId: "req_cc61_a",
    checkId: "chk_8814_1",
    verificationStatus: "FAIL",
    status: "open",
    caseStatus: "assigned",
    severity: "critical",
    ownerActorId: "act_pd",
    assignedActorId: "act_mw",
    openedAt: "2026-08-17T12:03:29Z",
    dueAt: "2026-08-20T17:00:00Z",
    title: "Two active accounts have no MFA factor enrolled",
    summary:
      "okta:user/00u4a91zzQ (svc-etl-runner, a service account) and okta:user/00u7c22kkR (d.mehta, contractor) are ACTIVE in Okta with mfa_factor_count = 0. The active policy CC6.1 v3 requires every ACTIVE account to carry at least one factor, with no service-account carve-out.",
    ask:
      "Decide whether the service account is a genuine exception to record, or a real gap to remediate. The contractor account has no argument for exception.",
    evidenceArtifactIds: ["art_okta_users_0817"],
    citationRefs: ["art_okta_users_0817#row/00u4a91zzQ", "art_okta_users_0817#row/00u7c22kkR"],
    authority: "deterministic",
    timeline: [
      {
        id: "ev_1",
        at: "2026-08-17T12:03:29Z",
        actorId: "act_sys",
        kind: "run_completed",
        title: "Run run_8814 completed — CC6.1 aggregate FAIL",
      },
      {
        id: "ev_2",
        at: "2026-08-17T12:03:29Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4401 opened at severity critical",
        note: "Severity inherited from the control's consequence (critical), not chosen by the engine.",
      },
      {
        id: "ev_3",
        at: "2026-08-17T12:41:00Z",
        actorId: "act_pd",
        kind: "assigned",
        title: "Assigned to Matt Waite for review",
      },
      {
        id: "ev_4",
        at: "2026-08-17T13:02:00Z",
        actorId: "act_pd",
        kind: "comment",
        title: "Priya Desai commented",
        note:
          "svc-etl-runner is a machine identity that authenticates with a rotated key, not interactively — it cannot enrol a factor. I think that is an exception, not a gap. d.mehta is a real miss; offboarding was 2026-08-04 and the account should have been deactivated.",
      },
    ],
  },
  {
    id: "fnd_4402",
    ref: "F-4402",
    runId: "run_8816",
    controlId: "ctl_cc66",
    requirementId: "req_cc66_a",
    checkId: "chk_8816_1",
    verificationStatus: "FAIL",
    status: "open",
    caseStatus: "remediation_requested",
    severity: "critical",
    ownerActorId: "act_sk",
    assignedActorId: "act_mw",
    openedAt: "2026-08-17T13:11:38Z",
    dueAt: "2026-08-18T17:00:00Z",
    title: "S3 bucket grants read to a wildcard principal",
    summary:
      "northwind-analytics-scratch has a bucket policy granting s3:GetObject to Principal \"*\". The bucket holds 14 GB of exported event data. 1 of 236 buckets is affected.",
    ask: "Confirm the bucket policy has been corrected, then re-verify to close.",
    evidenceArtifactIds: ["art_aws_bucket_0817"],
    citationRefs: ["art_aws_bucket_0817#bucket/northwind-analytics-scratch"],
    authority: "deterministic",
    timeline: [
      {
        id: "ev_10",
        at: "2026-08-17T13:11:38Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4402 opened at severity critical",
      },
      {
        id: "ev_11",
        at: "2026-08-17T13:20:00Z",
        actorId: "act_mw",
        kind: "remediation_requested",
        title: "Remediation requested from Sam Okonkwo",
        note: "Public read on an event-data bucket is not defensible. Please remove the wildcard grant today and reply here with the change reference.",
      },
      {
        id: "ev_12",
        at: "2026-08-17T14:02:00Z",
        actorId: "act_sk",
        kind: "comment",
        title: "Sam Okonkwo commented",
        note: "Wildcard grant removed in change CHG-2288 at 13:58 UTC. The bucket is now private. Next AWS collection is 16:10 UTC — re-verify after that and it should close.",
      },
    ],
  },
  {
    id: "fnd_4403",
    ref: "F-4403",
    runId: "run_8816",
    controlId: "ctl_cc66",
    requirementId: "req_cc66_b",
    checkId: "chk_8816_2",
    verificationStatus: "FAIL",
    status: "open",
    caseStatus: "escalated",
    severity: "critical",
    ownerActorId: "act_sk",
    assignedActorId: "act_jl",
    openedAt: "2026-08-17T13:11:38Z",
    dueAt: "2026-08-18T17:00:00Z",
    title: "Security group exposes Postgres to the public internet",
    summary:
      "sg-0b41e77a9 (rds-legacy-access) permits inbound 0.0.0.0/0 on TCP 5432. The group is attached to one RDS instance, northwind-legacy-reporting.",
    ask:
      "The control owner argues this is compensated by database-level IP allowlisting. That disagreement is escalated to the workspace owner to settle.",
    evidenceArtifactIds: ["art_aws_sg_0817"],
    citationRefs: ["art_aws_sg_0817#sg/sg-0b41e77a9"],
    authority: "deterministic",
    timeline: [
      {
        id: "ev_20",
        at: "2026-08-17T13:11:38Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4403 opened at severity critical",
      },
      {
        id: "ev_21",
        at: "2026-08-17T13:26:00Z",
        actorId: "act_sk",
        kind: "exception_requested",
        title: "Sam Okonkwo requested an exception",
        note: "The instance enforces an IP allowlist at the database level, so the open security group is not exploitable in practice. Requesting an exception until the legacy reporting stack is decommissioned in Q4.",
      },
      {
        id: "ev_22",
        at: "2026-08-17T13:44:00Z",
        actorId: "act_mw",
        kind: "reject_remediation",
        title: "Matt Waite rejected the exception rationale",
        note: "A database-level allowlist is a different control and is not in this control's scope or evidence. Accepting this exception would mean the receipt claims something the evidence does not show. Escalating rather than deciding alone.",
      },
      {
        id: "ev_23",
        at: "2026-08-17T13:45:00Z",
        actorId: "act_mw",
        kind: "escalated",
        title: "Escalated to Jordan Lee (owner)",
        note: "Reviewer and control owner disagree on whether a compensating control outside this control's evidence can justify an exception. Needs an owner decision.",
      },
    ],
  },
  {
    id: "fnd_4404",
    ref: "F-4404",
    runId: "run_8818",
    controlId: "ctl_cc62",
    requirementId: "req_cc62_a",
    checkId: "chk_8818_3",
    verificationStatus: "UNKNOWN",
    status: "open",
    caseStatus: "open",
    severity: "high",
    ownerActorId: "act_pd",
    assignedActorId: "act_mw",
    openedAt: "2026-08-17T13:51:12Z",
    dueAt: "2026-08-21T17:00:00Z",
    title: "Approval records unreadable for 43 of 68 access grants",
    summary:
      "The Jira connection is missing the read:jira-user scope, so the approver on an access-request ticket cannot be resolved. 43 of 68 grants in the period therefore have no evaluable approval record. This is UNKNOWN, not FAIL — the control may well be operating; we cannot currently see it.",
    ask:
      "Fix the source (grant the missing scope and re-collect), or accept a scope limitation on the record. Do not decide the control on this evidence.",
    evidenceArtifactIds: ["art_jira_grants_0815"],
    citationRefs: ["art_jira_grants_0815#tickets"],
    authority: "deterministic",
    timeline: [
      {
        id: "ev_30",
        at: "2026-08-17T13:51:12Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4404 opened as UNKNOWN, severity high",
        note: "The engine does not convert missing evidence into a failure. UNKNOWN is the honest answer and it blocks a receipt just as firmly.",
      },
    ],
  },
  {
    id: "fnd_4405",
    ref: "F-4405",
    runId: "run_8818",
    controlId: "ctl_cc72",
    requirementId: "req_cc72_a",
    checkId: "chk_8818_8",
    verificationStatus: "UNKNOWN",
    status: "open",
    caseStatus: "open",
    severity: "high",
    ownerActorId: "act_sk",
    assignedActorId: "act_mw",
    openedAt: "2026-08-17T13:51:12Z",
    dueAt: "2026-08-19T17:00:00Z",
    title: "Monitoring evidence is 8 days stale and the source is revoked",
    summary:
      "The Vanta API token was revoked on 2026-08-11 during a credential rotation and never replaced. The newest alert register is from 2026-08-09, against a 2-day freshness SLA. A semantic worker read the 40 sampled dispositions and judged \"support\" with citations — but a semantic judgment can never issue an authoritative PASS, so the check routed here.",
    ask: "Reconnect the source and re-collect, or record a scope limitation for the period.",
    evidenceArtifactIds: ["art_vanta_alerts_0809"],
    citationRefs: ["art_vanta_alerts_0809#alert/AL-2291"],
    authority: "semantic",
    semanticRoute: "cited_support_needs_review",
    timeline: [
      {
        id: "ev_40",
        at: "2026-08-17T13:50:20Z",
        actorId: "act_sys",
        kind: "comment",
        title: "Semantic worker returned judgment: support",
        note: "Route cited_support_needs_review. 40 of 40 sampled alerts carry a disposition inside one business day, each cited to a row in the register. The worker's authoritativeStatus is UNKNOWN by construction — the model is not allowed to pass a control.",
      },
      {
        id: "ev_41",
        at: "2026-08-17T13:51:12Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4405 opened as UNKNOWN, severity high",
      },
    ],
  },
  /* ── Closed items — these give the queue a "done" tab with real content ── */
  {
    id: "fnd_4388",
    ref: "F-4388",
    runId: "run_8812",
    controlId: "ctl_cc63",
    requirementId: "req_cc63_a",
    // The check in run_8812 (its own run), not the one in the August sweep.
    checkId: "chk_8812_5",
    verificationStatus: "FAIL",
    status: "remediated",
    caseStatus: "closed",
    severity: "critical",
    ownerActorId: "act_sk",
    assignedActorId: "act_mw",
    openedAt: "2026-08-01T02:04:41Z",
    dueAt: "2026-08-05T17:00:00Z",
    title: "One termination deprovisioned 61 hours after the HRIS date",
    summary:
      "Worker 4318 was terminated 2026-07-28 02:00 UTC and the Okta account was deactivated 2026-07-30 15:00 UTC — 61 hours, against a 24-hour requirement.",
    ask: "Closed — remediation approved and confirmed by re-verification.",
    evidenceArtifactIds: ["art_okta_users_0731"],
    citationRefs: ["art_okta_users_0731#row/00u2b18xxK"],
    authority: "deterministic",
    timeline: [
      {
        id: "ev_60",
        at: "2026-08-01T02:04:41Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4388 opened at severity critical",
      },
      {
        id: "ev_61",
        at: "2026-08-01T10:12:00Z",
        actorId: "act_mw",
        kind: "remediation_requested",
        title: "Remediation requested from Sam Okonkwo",
        note: "Please wire the HRIS termination webhook to the deprovisioning job so this is not a manual step.",
      },
      {
        id: "ev_62",
        at: "2026-08-06T15:30:00Z",
        actorId: "act_sk",
        kind: "comment",
        title: "Sam Okonkwo commented",
        note: "Webhook shipped in CHG-2201. Deprovisioning now fires within 5 minutes of the Workday event.",
      },
      {
        id: "ev_63",
        at: "2026-08-17T06:04:51Z",
        actorId: "act_sys",
        kind: "reverified",
        title: "Re-verification run run_8815 returned PASS",
        note: "All 19 terminations within 24h; slowest 19h 40m.",
      },
      {
        id: "ev_64",
        at: "2026-08-17T09:15:00Z",
        actorId: "act_mw",
        kind: "approve_remediation",
        title: "Matt Waite approved the remediation",
        note: "Closing. The fix is structural rather than a one-off correction, and the re-run confirms it across the whole population.",
      },
      {
        id: "ev_65",
        at: "2026-08-17T09:15:02Z",
        actorId: "act_sys",
        kind: "receipt_issued",
        title: "Receipt rcp_8815 issued",
      },
    ],
  },
  {
    id: "fnd_4372",
    ref: "F-4372",
    runId: "run_8811",
    controlId: "ctl_cc14",
    requirementId: "req_cc14_a",
    checkId: "chk_8811_10",
    verificationStatus: "UNKNOWN",
    status: "accepted_exception",
    caseStatus: "exception_accepted",
    severity: "medium",
    ownerActorId: "act_pd",
    assignedActorId: "act_jl",
    openedAt: "2026-07-01T02:03:58Z",
    dueAt: "2026-07-15T17:00:00Z",
    title: "Two pre-acquisition hires have no background check on file",
    summary:
      "Workers 2871 and 2903 joined via the Cedar acquisition in 2025 and pre-date Northwind's background-check programme. No record exists to collect.",
    ask: "Closed — exception accepted for the period with a scope note on the pack.",
    evidenceArtifactIds: ["art_hris_bgc_0731"],
    citationRefs: ["art_hris_bgc_0731#hires"],
    authority: "human",
    timeline: [
      {
        id: "ev_70",
        at: "2026-07-01T02:03:58Z",
        actorId: "act_sys",
        kind: "finding_opened",
        title: "Finding F-4372 opened as UNKNOWN, severity medium",
      },
      {
        id: "ev_71",
        at: "2026-07-03T11:00:00Z",
        actorId: "act_pd",
        kind: "exception_requested",
        title: "Priya Desai requested an exception",
        note: "Both workers pre-date the programme by way of the Cedar acquisition. There is no record to produce, and re-screening tenured staff was reviewed and declined by legal.",
      },
      {
        id: "ev_72",
        at: "2026-07-04T16:22:00Z",
        actorId: "act_jl",
        kind: "exception_accepted",
        title: "Jordan Lee accepted the exception",
        note: "Accepted for this period only, scoped to these two worker ids, and carried on the pack as a stated scope limitation rather than a passing control. Revisit at the next period boundary.",
      },
    ],
  },
];

export function finding(id: string): Finding | undefined {
  return FINDINGS.find((f) => f.id === id || f.ref === id);
}

/** Open work — what the decision inbox counts. */
export const OPEN_FINDINGS = FINDINGS.filter((f) => f.status === "open");

/** Assigned to me and still open — the rail's badge. */
export const MY_QUEUE = OPEN_FINDINGS.filter((f) => f.assignedActorId === ME.id);

/* ── Receipts ─────────────────────────────────────────────────────────────── */

export interface Receipt {
  id: string;
  runId: string;
  parentReceiptId: string | null;
  status: VerificationStatus;
  issuedAt: string;
  engineVersion: string;
  payloadHash: string;
  signatureKeyId: string;
  signatureAlgorithm: "HMAC-SHA256" | "Ed25519";
  checkCount: number;
  evidenceHashes: string[];
  policyVersionRefs: string[];
  replayVerified: boolean;
  replayCheckedAt: string | null;
  /** Present on a correction receipt. */
  supersedes?: string;
  correctionReason?: string;
}

const RECEIPT_FIXTURES: Omit<Receipt, "evidenceHashes" | "checkCount">[] = [
  {
    id: "rcp_8818",
    runId: "run_8818",
    parentReceiptId: null,
    status: "FAIL",
    issuedAt: "2026-08-17T13:51:14Z",
    engineVersion: "verifier@0.31.2",
    payloadHash: "sha256:b41c7e09a5d38f26c0b74e91a3d5f8c62a07b83e5d91f4c6a20b8e37d5c1a940",
    signatureKeyId: "key_nw_prod_2026q3",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc61_v3", "pol_cc62_v1", "pol_cc63_v2", "pol_cc66_v5", "pol_cc72_v1", "pol_cc81_v2", "pol_cc14_v1"],
    replayVerified: true,
    replayCheckedAt: "2026-08-17T13:52:00Z",
  },
  {
    id: "rcp_8817",
    runId: "run_8817",
    parentReceiptId: null,
    status: "PASS",
    issuedAt: "2026-08-17T13:41:46Z",
    engineVersion: "verifier@0.31.2",
    payloadHash: "sha256:2a8d5c07b3e91f46a0c85b3e79d1f4c62a8b05e37d9a1f4c6b02e8a5d93c17f4",
    signatureKeyId: "key_nw_prod_2026q3",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc81_v2"],
    replayVerified: true,
    replayCheckedAt: "2026-08-17T13:42:10Z",
  },
  {
    id: "rcp_8816",
    runId: "run_8816",
    parentReceiptId: null,
    status: "FAIL",
    issuedAt: "2026-08-17T13:11:40Z",
    engineVersion: "verifier@0.31.2",
    payloadHash: "sha256:6b02e8a5d93c17f42a8d5c07b3e91f46a0c85b3e79d1f4c62a8b05e37d9a1f4c",
    signatureKeyId: "key_nw_prod_2026q3",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc66_v5"],
    replayVerified: true,
    replayCheckedAt: "2026-08-17T13:12:00Z",
  },
  {
    id: "rcp_8815",
    runId: "run_8815",
    parentReceiptId: null,
    status: "PASS",
    issuedAt: "2026-08-17T06:04:53Z",
    engineVersion: "verifier@0.31.2",
    payloadHash: "sha256:d93c17f46b02e8a52a8d5c07b3e91f46a0c85b3e79d1f4c62a8b05e37d9a1f4c",
    signatureKeyId: "key_nw_prod_2026q3",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc63_v2"],
    replayVerified: true,
    replayCheckedAt: "2026-08-17T06:05:20Z",
  },
  {
    id: "rcp_8814",
    runId: "run_8814",
    parentReceiptId: null,
    status: "FAIL",
    issuedAt: "2026-08-17T12:03:31Z",
    engineVersion: "verifier@0.31.2",
    payloadHash: "sha256:37d9a1f4c6b02e8a5d93c17f42a8d5c07b3e91f46a0c85b3e79d1f4c62a8b05e",
    signatureKeyId: "key_nw_prod_2026q3",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc61_v3"],
    replayVerified: true,
    replayCheckedAt: "2026-08-17T12:04:00Z",
  },
  {
    id: "rcp_8812b",
    runId: "run_8812",
    parentReceiptId: "rcp_8812",
    status: "FAIL",
    issuedAt: "2026-08-04T11:20:00Z",
    engineVersion: "verifier@0.30.7",
    payloadHash: "sha256:f4c62a8b05e37d9a1f4c6b02e8a5d93c17f42a8d5c07b3e91f46a0c85b3e79d1",
    signatureKeyId: "key_nw_prod_2026q2",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc61_v3", "pol_cc63_v2", "pol_cc81_v2"],
    replayVerified: true,
    replayCheckedAt: "2026-08-04T11:22:00Z",
    supersedes: "rcp_8812",
    correctionReason:
      "The July sweep counted 3 deactivated accounts as ACTIVE because the extractor read a stale status field. Corrected by a CHILD receipt rather than by editing rcp_8812 — the ledger is append-only, so the original stays readable and the correction is linked to it.",
  },
  {
    id: "rcp_8812",
    runId: "run_8812",
    parentReceiptId: null,
    status: "FAIL",
    issuedAt: "2026-08-01T02:04:43Z",
    engineVersion: "verifier@0.30.7",
    payloadHash: "sha256:05e37d9a1f4c6b02e8a5d93c17f42a8d5c07b3e91f46a0c85b3e79d1f4c62a8b",
    signatureKeyId: "key_nw_prod_2026q2",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc61_v3", "pol_cc63_v2", "pol_cc81_v2"],
    replayVerified: true,
    replayCheckedAt: "2026-08-01T02:06:00Z",
  },
  {
    id: "rcp_8811",
    runId: "run_8811",
    parentReceiptId: null,
    status: "FAIL",
    issuedAt: "2026-07-01T02:04:00Z",
    engineVersion: "verifier@0.29.4",
    payloadHash: "sha256:91f46a0c85b3e79d1f4c62a8b05e37d9a1f4c6b02e8a5d93c17f42a8d5c07b3e",
    signatureKeyId: "key_nw_prod_2026q2",
    signatureAlgorithm: "Ed25519",
    policyVersionRefs: ["pol_cc61_v3", "pol_cc63_v2"],
    replayVerified: true,
    replayCheckedAt: "2026-07-01T02:07:00Z",
  },
];

/**
 * Artifacts, with `citedByReceiptIds` DERIVED from the receipt ledger.
 *
 * It used to be a hand-written field and five of the ten entries disagreed with
 * the receipts that actually pinned their hash — including one artifact whose
 * lineage claimed a citation from a receipt that pinned a different hash
 * entirely. The relationship has exactly one source of truth (a receipt's
 * `evidenceHashes`), so reading it the other way has to be computed, not typed.
 *
 * This is also what makes the "cannot redact — cited by a standing receipt" lock
 * on the artifact page honest rather than decorative.
 *
 * Declared AFTER `RECEIPTS` on purpose: a `const` read during module evaluation
 * must come after the one it reads, or the module throws on import.
 */
/**
 * Receipts, with `evidenceHashes` and `checkCount` DERIVED from the run.
 *
 * Both were hand-written and both drifted. `rcp_8818` claimed three evidence
 * hashes for a run that pinned nine artifacts, and the offline-verify transcript
 * on its page printed that three while the run page listed all nine. Seven
 * receipts claimed check counts for runs that carried no checks at all.
 *
 * A receipt's whole purpose is to bind a verdict to exactly what produced it, so
 * a fixture where the binding is typed separately from the thing bound is the one
 * shape this file must not have. Both fields now come from the run.
 */
export const RECEIPTS: Receipt[] = RECEIPT_FIXTURES.map((r) => {
  const source = RUNS.find((x) => x.id === r.runId);
  return {
    ...r,
    evidenceHashes: (source?.evidenceArtifactIds ?? [])
      .map((id) => ARTIFACT_FIXTURES.find((a) => a.id === id)?.contentHash)
      .filter((h): h is string => Boolean(h)),
    checkCount: CHECKS.filter((c) => c.runId === r.runId).length,
  };
});

export const ARTIFACTS: EvidenceArtifact[] = ARTIFACT_FIXTURES.map((a) => ({
  ...a,
  citedByReceiptIds: RECEIPTS.filter((r) => r.evidenceHashes.includes(a.contentHash)).map((r) => r.id),
}));

export function receipt(id: string): Receipt | undefined {
  return RECEIPTS.find((r) => r.id === id);
}

/* ── Export packs ──────────────────────────────────────────────────────────
   ONE word for this object, everywhere: "pack".

   The engine's type is `VerificationExportPacket` and the docs use both "evidence
   pack" and "audit-preparation packet". The UI used to mix them — nav said
   "Evidence packs" while half the prose said "packet" — which is exactly the
   vocabulary drift this product cannot afford, since its pitch is that a term
   means one thing. "Pack" is the user-facing word; `VerificationExportPacket`
   stays the engine's type name.
   ─────────────────────────────────────────────────────────────────────────── */

export interface Pack {
  id: string;
  label: string;
  kind: "audit_preparation" | "grc_exchange" | "offline_replay";
  createdAt: string;
  createdByActorId: string;
  expiresAt: string | null;
  runIds: string[];
  controlIds: string[];
  receiptIds: string[];
  artifactCount: number;
  sizeBytes: number;
  redactionCount: number;
  bundleHash: string;
  offlineVerified: boolean;
  /** Scope notes the pack carries — the honest half of an export. */
  scopeLimitations: string[];
  sharedWith: { actorId: string; grantedAt: string; expiresAt: string; views: number; downloads: number }[];
}

export const PACKS: Pack[] = [
  {
    id: "pkt_0031",
    label: "Brightline LLP — interim evidence, Aug 2026",
    kind: "audit_preparation",
    createdAt: "2026-08-17T09:40:00Z",
    createdByActorId: "act_mw",
    expiresAt: "2026-08-24T09:40:00Z",
    // `run_8812`, not `run_8812b` — that is a RECEIPT id. The artifact count
    // below only adds up with the run.
    runIds: ["run_8815", "run_8817", "run_8812"],
    controlIds: ["ctl_cc63", "ctl_cc81", "ctl_cc14"],
    receiptIds: ["rcp_8815", "rcp_8817", "rcp_8812b"],
    artifactCount: 5,
    sizeBytes: 4_218_884,
    redactionCount: 12,
    bundleHash: "sha256:aa17c4e93b5d81f26e0a3c7b94d5f812a6c0b39e7a58d1f4c6b23e0a9d78c5b31",
    offlineVerified: true,
    scopeLimitations: [
      "CC1.4 carries an accepted exception for two pre-acquisition workers (F-4372). The control is not represented as passing.",
      "CC6.1, CC6.2, CC6.6, CC7.2 and A1.2 are excluded — they are FAIL, UNKNOWN or NOT RUN as of the export date and cannot be evidenced.",
    ],
    sharedWith: [
      {
        actorId: "act_ext",
        grantedAt: "2026-08-17T09:42:00Z",
        expiresAt: "2026-08-24T09:42:00Z",
        views: 7,
        downloads: 2,
      },
    ],
  },
  {
    id: "pkt_0030",
    label: "July period close — full ledger",
    kind: "offline_replay",
    createdAt: "2026-08-04T12:00:00Z",
    createdByActorId: "act_jl",
    expiresAt: null,
    runIds: ["run_8811", "run_8812"],
    controlIds: CONTROLS.map((c) => c.id),
    receiptIds: ["rcp_8811", "rcp_8812", "rcp_8812b"],
    artifactCount: 2,
    sizeBytes: 1_104_220,
    redactionCount: 4,
    bundleHash: "sha256:bb28d5f04c6e92370b1d48c8a5e6f923b7d1c40f8b69e2d5a37c1f8b6e4d2a05",
    offlineVerified: true,
    scopeLimitations: [
      "Receipt rcp_8812 is superseded by the correction receipt rcp_8812b. Both are included; the replay tool follows the parent link.",
    ],
    sharedWith: [],
  },
  {
    id: "pkt_0029",
    label: "Vanta exchange — control mapping export",
    kind: "grc_exchange",
    createdAt: "2026-07-28T16:12:00Z",
    createdByActorId: "act_sk",
    expiresAt: "2026-10-28T16:12:00Z",
    runIds: ["run_8811"],
    controlIds: ["ctl_cc61", "ctl_cc63", "ctl_cc81"],
    receiptIds: ["rcp_8811"],
    artifactCount: 1,
    sizeBytes: 288_140,
    redactionCount: 0,
    bundleHash: "sha256:cc39e6a15d7f03481c2e59d9b6f70a34c8e2d51f9c70f3e6b48d2a9c7f5e3b16",
    offlineVerified: true,
    scopeLimitations: [],
    sharedWith: [],
  },
];

export function pack(id: string): Pack | undefined {
  return PACKS.find((p) => p.id === id);
}

/* ── Workflow candidates (the "connect a workflow" path) ─────────────────── */

export interface Candidate {
  id: string;
  name: string;
  actor: string;
  verifier: string;
  oracle: string | null;
  oracleIsIndependent: boolean;
  consequence: Consequence;
  evidence: string;
  status: CandidateStatus;
  createdAt: string;
  createdByActorId: string;
  rejectionReason?: string;
  /** Set once admitted — where the workflow landed. */
  admittedControlId?: string;
}

export const CANDIDATES: Candidate[] = [
  {
    id: "cnd_0071",
    name: "Refund above $5,000 was actually issued",
    actor: "billing-agent (automated)",
    verifier: "Payments oracle",
    oracle: "Stripe API — refund.status == \"succeeded\" and amount matches the approved figure",
    oracleIsIndependent: true,
    consequence: "critical",
    evidence: "Stripe refund object, approval ticket, ledger entry",
    status: "admitted",
    createdAt: "2026-08-12T10:00:00Z",
    createdByActorId: "act_jl",
    admittedControlId: "ctl_cc81",
  },
  {
    id: "cnd_0072",
    name: "Access request was approved by the resource owner",
    actor: "access-bot (automated)",
    verifier: "Jira + Okta cross-check",
    oracle: "Jira approval record timestamp compared against the Okta grant event",
    oracleIsIndependent: true,
    consequence: "high",
    evidence: "Jira ticket, Okta system log",
    status: "admitted",
    createdAt: "2026-08-13T14:30:00Z",
    createdByActorId: "act_pd",
    admittedControlId: "ctl_cc62",
  },
  {
    id: "cnd_0073",
    name: "Code review was thorough",
    actor: "review-agent (LLM)",
    verifier: "review-agent (LLM)",
    oracle: null,
    oracleIsIndependent: false,
    consequence: "medium",
    evidence: "The agent's own summary of its review",
    status: "needs-oracle",
    createdAt: "2026-08-15T11:20:00Z",
    createdByActorId: "act_jl",
    rejectionReason:
      "The actor and the verifier are the same agent, and the only evidence is that agent's own account of its work. There is no external truth source, so admitting this would produce receipts that assert nothing checkable. Name an independent oracle — approving reviewer identity, a test suite result, a post-merge defect signal — or leave it out.",
  },
  {
    id: "cnd_0074",
    name: "Customer data export honoured a deletion request",
    actor: "privacy-agent (automated)",
    verifier: "Storage + database cross-check",
    oracle: "Row absence in the primary database AND object absence in the storage bucket, 30 days after the request",
    oracleIsIndependent: true,
    consequence: "critical",
    evidence: "Deletion request record, DB query result, bucket listing",
    status: "draft",
    createdAt: "2026-08-16T16:45:00Z",
    createdByActorId: "act_pd",
  },
  {
    id: "cnd_0068",
    name: "On-call acknowledged the page within SLA",
    actor: "Human on-call engineer",
    verifier: "PagerDuty oracle",
    oracle: "PagerDuty incident acknowledged_at minus created_at",
    oracleIsIndependent: true,
    consequence: "medium",
    evidence: "PagerDuty incident log",
    status: "reject",
    createdAt: "2026-08-04T09:00:00Z",
    createdByActorId: "act_sk",
    rejectionReason:
      "Withdrawn by the author — PagerDuty is not in the SOC 2 scope for this period, so the workflow is real and well-specified but out of scope. Re-open at the next period boundary.",
  },
];

export function candidate(id: string): Candidate | undefined {
  return CANDIDATES.find((c) => c.id === id);
}

/* ── Semantic workers ────────────────────────────────────────────────────── */

export interface SemanticWorker {
  id: string;
  role: "semantic-check" | "connector-sync" | "receipt-replay" | "backfill" | "audit-export";
  model: { provider: string; name: string; version: string };
  calibrated: boolean;
  status: "healthy" | "starting" | "stopped" | "failed";
  lastHeartbeatAt: string;
  inBoundary: boolean;
  activeJobId: string | null;
  queued: number;
  succeeded24h: number;
  failed24h: number;
}

export const WORKERS: SemanticWorker[] = [
  {
    id: "wkr_sem_1",
    role: "semantic-check",
    model: { provider: "in-boundary", name: "qwen3-32b-instruct", version: "2026.05" },
    calibrated: true,
    status: "healthy",
    lastHeartbeatAt: "2026-08-17T14:19:40Z",
    inBoundary: true,
    activeJobId: null,
    queued: 3,
    succeeded24h: 118,
    failed24h: 2,
  },
  {
    id: "wkr_sem_2",
    role: "semantic-check",
    model: { provider: "in-boundary", name: "qwen3-32b-instruct", version: "2026.07" },
    calibrated: false,
    status: "healthy",
    lastHeartbeatAt: "2026-08-17T14:19:12Z",
    inBoundary: true,
    activeJobId: null,
    queued: 0,
    succeeded24h: 0,
    failed24h: 0,
  },
  {
    id: "wkr_sync_1",
    role: "connector-sync",
    model: { provider: "—", name: "deterministic", version: "—" },
    calibrated: true,
    status: "healthy",
    lastHeartbeatAt: "2026-08-17T14:20:00Z",
    inBoundary: true,
    activeJobId: "job_aws_0817_1610",
    queued: 1,
    succeeded24h: 46,
    failed24h: 3,
  },
  {
    id: "wkr_replay_1",
    role: "receipt-replay",
    model: { provider: "—", name: "deterministic", version: "—" },
    calibrated: true,
    status: "healthy",
    lastHeartbeatAt: "2026-08-17T14:18:02Z",
    inBoundary: true,
    activeJobId: null,
    queued: 0,
    succeeded24h: 8,
    failed24h: 0,
  },
];

export interface SemanticJudgment {
  id: string;
  checkId: string;
  controlId: string;
  workerId: string;
  judgment: SemanticJudgmentKind;
  route: SemanticRoute;
  /** Never "PASS" — the type in the real engine forbids it. */
  authoritativeStatus: "FAIL" | "UNKNOWN";
  rationale: string;
  citations: { artifactId: string; quote: string }[];
  createdAt: string;
  promptVersion: string;
}

export const JUDGMENTS: SemanticJudgment[] = [
  {
    id: "sjd_0912",
    checkId: "chk_8818_8",
    controlId: "ctl_cc72",
    workerId: "wkr_sem_1",
    judgment: "support",
    route: "cited_support_needs_review",
    authoritativeStatus: "UNKNOWN",
    rationale:
      "All 40 sampled alerts carry a disposition field with a reviewer name and a timestamp inside one business day of the alert being raised. The register's own schema documents the disposition workflow. I can cite each one. I cannot speak to the 1,244 alerts outside the sample, and the register itself is 8 days stale.",
    citations: [
      {
        artifactId: "art_vanta_alerts_0809",
        quote: "AL-2291 · raised 2026-08-07T22:14Z · dispositioned 2026-08-08T09:02Z by s.okonkwo · \"false positive, scanner IP\"",
      },
      {
        artifactId: "art_vanta_alerts_0809",
        quote: "AL-2288 · raised 2026-08-07T11:40Z · dispositioned 2026-08-07T14:55Z by s.okonkwo · \"patched, see CHG-2240\"",
      },
    ],
    createdAt: "2026-08-17T13:50:20Z",
    promptVersion: "semantic-check@v11",
  },
  // Note there is NO judgment row for the CC6.2 checks: those are deterministic,
  // and a semantic judgment attached to a deterministic check would make the
  // finding page claim "a model looked at this" beside a "Deterministic" badge.
  {
    id: "sjd_0908_unused",
    checkId: "chk_8818_3",
    controlId: "ctl_cc62",
    workerId: "wkr_sem_1",
    judgment: "unknown",
    route: "unknown_uncited",
    authoritativeStatus: "UNKNOWN",
    rationale:
      "The ticket export contains request records but no approver identity field, so I have nothing to quote for the approval requirement. I will not infer approval from the presence of a ticket.",
    citations: [],
    createdAt: "2026-08-17T13:49:50Z",
    promptVersion: "semantic-check@v11",
  },
];

/* ── Scorecard (the nine required metrics) ───────────────────────────────── */

export interface Metric {
  id: string;
  label: string;
  value: string;
  numeric: number;
  target: string;
  status: MetricStatus;
  direction: "higher-is-better" | "lower-is-better";
  note: string;
}

export const METRICS: Metric[] = [
  {
    id: "verified_coverage",
    label: "Verified coverage",
    // 3 of 8 — this MUST agree with `controlRollup().PASS`, which the Overview page
    // renders from `CONTROLS` independently. A scorecard that disagrees with the
    // control list two screens away is the first thing a reviewer catches.
    value: "37.5%",
    numeric: 37.5,
    target: "≥ 90%",
    status: "fail",
    direction: "higher-is-better",
    note: "3 of 8 controls carry a current PASS receipt. CC6.1 and CC6.6 are FAIL, CC6.2 and CC7.2 are UNKNOWN, and A1.2 has never run.",
  },
  {
    id: "false_accept_rate",
    label: "False accept rate",
    value: "0.0%",
    numeric: 0,
    target: "≤ 1%",
    status: "pass",
    direction: "lower-is-better",
    note: "No accepted finding has later been reversed. Measured over 43 decisions since 2026-05-01.",
  },
  {
    id: "unknown_rate",
    label: "UNKNOWN rate",
    // 3 of 11 on the current sweep, and all three trace to just two broken
    // sources: a missing Jira scope and a revoked Vanta token. Not one of them is
    // a control that failed — which is the point of counting UNKNOWN separately.
    value: "27.3%",
    numeric: 27.3,
    target: "≤ 10%",
    status: "fail",
    direction: "lower-is-better",
    note: "3 of 11 checks on the current sweep returned UNKNOWN, sharing two root causes: a missing Jira scope and a revoked Vanta token. Fix the sources and all three resolve.",
  },
  {
    id: "override_rate",
    label: "Override rate",
    value: "4.7%",
    numeric: 4.7,
    target: "≤ 10%",
    status: "pass",
    direction: "lower-is-better",
    note: "2 of 43 decisions overrode an engine result across the period. One is the accepted exception on CC1.4, with a named approver.",
  },
  {
    id: "review_time_hours",
    label: "Median review time",
    // 238.8 h is the median of this workspace's two closed findings (391.2 h for
    // F-4388, 86.3 h for F-4372). It read 6.2 h, which no pair of fixture dates
    // produces — and a review-time metric that flatters itself is the one thing a
    // scorecard measuring reviewer honesty cannot do.
    value: "238.8 h",
    numeric: 238.8,
    target: "≤ 24 h",
    status: "fail",
    direction: "lower-is-better",
    note: "Median from finding opened to decision recorded, over the two findings closed in this period. Both waited on a structural fix rather than on a reviewer.",
  },
  {
    id: "freshness_sla",
    label: "Evidence freshness SLA",
    // 4 of 6, not 5: Jira is 53h old against a 24h SLA as well as Vanta being 8
    // days old against 2. The Connectors page computes this per source and would
    // have printed "breached" on a card this metric counted as healthy.
    value: "66.7%",
    numeric: 66.7,
    target: "≥ 95%",
    status: "fail",
    direction: "higher-is-better",
    note: "4 of 6 sources are inside their freshness window. Jira is 2 days stale on a 1-day SLA; Vanta has been blocked for 8 days on a 2-day SLA.",
  },
  {
    id: "population_coverage",
    label: "Population coverage",
    // Item-weighted: 2,758 of 4,049 population items were actually tested.
    // The old 97.1% matched no definition — not item-weighted, not
    // control-weighted — and claimed 6 of 8 controls covered the full population
    // when it is 5, because A1.2 tests 0 of 4.
    value: "68.1%",
    numeric: 68.1,
    target: "≥ 95%",
    status: "fail",
    direction: "higher-is-better",
    note: "5 of 8 controls test their full population. CC6.2 samples 25 of 68 and CC7.2 samples 40 of 1,284; A1.2 has never run.",
  },
  {
    id: "receipt_replay_success",
    label: "Receipt replay success",
    value: "100%",
    numeric: 100,
    target: "100%",
    status: "pass",
    direction: "higher-is-better",
    note: "All 8 receipts re-derive their recorded verdict offline from the packed evidence.",
  },
  {
    id: "silent_approve_rate",
    label: "Silent approve rate",
    value: "0.0%",
    numeric: 0,
    target: "0%",
    status: "pass",
    direction: "lower-is-better",
    note: "No finding has been closed without a recorded rationale. This is a hard gate, not a target.",
  },
];

/* ── Runtime & residency ─────────────────────────────────────────────────── */

export interface RuntimeCheck {
  id: string;
  label: string;
  status: "pass" | "warn" | "fail";
  detail: string;
}

export const RUNTIME_CHECKS: RuntimeCheck[] = [
  {
    id: "rt_boundary",
    label: "Raw evidence never leaves the boundary",
    status: "pass",
    detail: `All ${ARTIFACT_FIXTURES.length} artifacts are stored inside the customer VPC. Only receipt hashes and verdicts cross the sync boundary.`,
  },
  {
    id: "rt_keys",
    label: "Signing key custody",
    status: "pass",
    detail: "key_nw_prod_2026q3 is customer-held in AWS KMS (alias/northwind-verification). Excellent cannot sign on your behalf.",
  },
  {
    id: "rt_rotation",
    label: "Key rotation proof",
    status: "warn",
    detail: "Last rotation 2026-07-01, 47 days ago. Policy is 90 days, so this is inside the window but the next rotation is unscheduled.",
  },
  {
    id: "rt_replay",
    label: "Offline replay",
    status: "pass",
    detail: "8 receipts replay-verified. The replay bundle runs with no network access and no Excellent service reachable.",
  },
  {
    id: "rt_sync",
    label: "Sync freshness",
    status: "pass",
    detail: "Last successful sync 2026-08-17T14:12Z, 8 minutes ago. 0 pending outbox events.",
  },
  {
    id: "rt_judge",
    label: "Judge model weights",
    status: "pass",
    detail: "qwen3-32b-instruct 2026.05 runs in-boundary on open weights. No closed-weight model is reachable from the judge path.",
  },
];

/* ── Derived rollups (single source for every screen's numbers) ─────────── */

export interface StatusRollup {
  PASS: number;
  FAIL: number;
  UNKNOWN: number;
  NOT_RUN: number;
  total: number;
}

export function controlRollup(): StatusRollup {
  const r: StatusRollup = { PASS: 0, FAIL: 0, UNKNOWN: 0, NOT_RUN: 0, total: CONTROLS.length };
  for (const c of CONTROLS) r[c.status] += 1;
  return r;
}



/** Sources that need attention — drives the connectors badge. */
export const UNHEALTHY_SOURCES = SOURCES.filter((s) => s.health !== "healthy");

/** Drafts that cannot activate — drives the policies badge. */
export const BLOCKED_POLICIES = POLICIES.filter((p) => p.status === "draft" && p.blockedReason);

/** Candidates that still need an independent oracle — drives the workflows badge. */
export const CANDIDATES_NEEDING_ORACLE = CANDIDATES.filter((c) => c.status === "needs-oracle" || c.status === "draft");

/* ── Formatting ──────────────────────────────────────────────────────────── */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `17 Aug 2026` — never shows seconds, matching the repo's date rule. */
export function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** `17 Aug 2026, 13:51` — minutes precision, UTC, never seconds. */
export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${fmtDate(iso)}, ${hh}:${mm} UTC`;
}

/** `4h ago` / `in 3d`. Measured against the frozen `NOW`, so it never drifts. */
export function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const delta = NOW.getTime() - then;
  const future = delta < 0;
  const abs = Math.abs(delta);
  const mins = Math.round(abs / 60_000);
  let out: string;
  if (mins < 1) out = "just now";
  else if (mins < 60) out = `${mins}m`;
  else if (mins < 60 * 24) out = `${Math.round(mins / 60)}h`;
  else if (mins < 60 * 24 * 30) out = `${Math.round(mins / (60 * 24))}d`;
  else out = `${Math.round(mins / (60 * 24 * 30))}mo`;
  if (out === "just now") return out;
  return future ? `in ${out}` : `${out} ago`;
}

/** `4.2 MB`. Two significant places, so a table column stays narrow. */
export function fmtBytes(n: number): string {
  if (n < 1_024) return `${n} B`;
  if (n < 1_024 * 1_024) return `${(n / 1_024).toFixed(1)} KB`;
  return `${(n / (1_024 * 1_024)).toFixed(1)} MB`;
}

/** `1,907`. Grouped, so a population figure is readable at a glance. */
export function fmtNumber(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * `May – Jul 2026` — a compact observation window.
 *
 * The rail's tenant chip is 248px wide and has to carry the framework name too, so
 * two full dates ("1 May 2026 – 31 Jul 2026") get ellipsised right where the end
 * date should be — leaving a window with no end, which is worse than a rounder one.
 * Month precision is the right resolution for a period label anyway; the exact
 * boundary dates are on the Overview page's lede where there is room for them.
 */
export function fmtPeriod(startIso: string, endIso: string): string {
  const a = new Date(startIso);
  const b = new Date(endIso);
  const yA = a.getUTCFullYear();
  const yB = b.getUTCFullYear();
  const mA = MONTHS[a.getUTCMonth()];
  const mB = MONTHS[b.getUTCMonth()];
  return yA === yB ? `${mA} – ${mB} ${yB}` : `${mA} ${yA} – ${mB} ${yB}`;
}

/** First 10 significant characters of a `sha256:…` digest, plus an ellipsis. */
export function shortHash(hash: string): string {
  const body = hash.startsWith("sha256:") ? hash.slice(7) : hash;
  return `${body.slice(0, 10)}…`;
}

/* ── Status → tone / glyph / label ──────────────────────────────────────── */

export const STATUS_TONE: Record<VerificationStatus, Tone> = {
  PASS: "pass",
  FAIL: "fail",
  UNKNOWN: "unknown",
  NOT_RUN: "neutral",
};

/** Distinct SHAPE per status, so verdicts survive greyscale and colour blindness. */
export const STATUS_GLYPH: Record<VerificationStatus, string> = {
  PASS: "✓",
  FAIL: "✕",
  UNKNOWN: "?",
  NOT_RUN: "–",
};

export const STATUS_LABEL: Record<VerificationStatus, string> = {
  PASS: "PASS",
  FAIL: "FAIL",
  UNKNOWN: "UNKNOWN",
  NOT_RUN: "NOT RUN",
};

export const HEALTH_TONE: Record<SourceHealth, Tone> = {
  healthy: "pass",
  stale: "unknown",
  blocked: "fail",
  revoked: "fail",
  paused: "neutral",
};

export const METRIC_TONE: Record<MetricStatus, Tone> = {
  pass: "pass",
  warn: "unknown",
  fail: "fail",
  blocked: "neutral",
};

export const CASE_STATUS_LABEL: Record<ReviewCaseStatus, string> = {
  open: "Open",
  assigned: "Assigned",
  remediation_requested: "Remediation requested",
  exception_requested: "Exception requested",
  exception_accepted: "Exception accepted",
  escalated: "Escalated",
  ready_for_reverification: "Ready to re-verify",
  closed: "Closed",
};

export const CASE_STATUS_TONE: Record<ReviewCaseStatus, Tone> = {
  open: "neutral",
  assigned: "accent",
  remediation_requested: "unknown",
  exception_requested: "unknown",
  exception_accepted: "waived",
  escalated: "fail",
  ready_for_reverification: "running",
  closed: "pass",
};

export const AUTHORITY_LABEL: Record<Authority, string> = {
  deterministic: "Deterministic",
  semantic: "Semantic (model)",
  human: "Human",
  imported: "Imported",
};

export const DECISION_LABEL: Record<ReviewDecisionKind, string> = {
  accept_exception: "Accept as exception",
  request_evidence: "Request more evidence",
  approve_remediation: "Approve remediation",
  reject_remediation: "Reject remediation",
  dismiss: "Dismiss finding",
};

export const DECISION_HELP: Record<ReviewDecisionKind, string> = {
  accept_exception:
    "The control did not operate as written, and you are recording a reasoned, scoped exception. The pack will carry this as a stated limitation — never as a pass.",
  request_evidence:
    "You cannot decide on what is here. This sends the finding back to the owner naming the evidence you need, and leaves the control UNKNOWN.",
  approve_remediation:
    "The gap has been fixed and a re-verification run confirms it. This closes the finding and lets the run issue a receipt.",
  reject_remediation:
    "The claimed fix does not satisfy the requirement. The finding returns to the owner with your reasoning attached.",
  dismiss:
    "The finding is not valid — a policy bug, a duplicate, or out of scope. Requires a rationale, and is counted in the override rate.",
};

export const CANDIDATE_STATUS_LABEL: Record<CandidateStatus, string> = {
  draft: "Draft",
  "needs-oracle": "Needs an oracle",
  admitted: "Admitted",
  reject: "Not admitted",
};

export const CANDIDATE_STATUS_TONE: Record<CandidateStatus, Tone> = {
  draft: "neutral",
  "needs-oracle": "unknown",
  admitted: "pass",
  reject: "neutral",
};

export const SEMANTIC_ROUTE_LABEL: Record<SemanticRoute, string> = {
  cited_support_needs_review: "Cited support → human review",
  cited_conflict_exception: "Cited conflict → exception",
  unknown_uncited: "Unknown, no citation",
  unknown_model_reported: "Unknown, model declined",
  high_consequence_support_needs_human: "High consequence → human required",
  uncalibrated_model_needs_review: "Uncalibrated model → review",
};
